// Fechar uma licitação pede o que depois responde "onde o funil vaza":
//  • Perdido: motivo padronizado + quem ganhou + por quanto (se souber);
//  • Não participamos: motivo padronizado;
//  • Contrato assinado: valor contratado (sugerido o estimado).
// O servidor recusa Perdido/Não participamos sem motivo; aqui só se monta o
// texto. Motivo "Outro" exige detalhe.
import { useState } from 'react';
import { Modal, Radio, Input, InputNumber, Typography, Space, Form } from 'antd';
import { MOTIVOS_PERDA, MOTIVOS_NAO_PARTICIPAR } from './etapas';
import { PROPS_DINHEIRO_BR } from '../../../utils/dinheiroInput';

const { Text } = Typography;

export type TipoFechamento = 'perdido' | 'nao_participamos' | 'ganho';

export interface DadosFechamento {
  motivo?: string;
  vencedor?: string;
  preco_vencedor?: number | null;
  valor_contratado?: number | null;
}

/** Junta motivo e detalhe no texto que vai para o campo (teto 300). */
export function textoDoMotivo(motivo: string, detalhe: string): string {
  const d = String(detalhe || '').trim();
  const base = motivo === 'Outro motivo' && d ? d : (d ? `${motivo} — ${d}` : motivo);
  return base.slice(0, 300);
}

export function ModalFecharLicitacao({ tipo, titulo, valorEstimado, onCancelar, onConfirmar }: {
  tipo: TipoFechamento;
  titulo: string;
  valorEstimado?: number | null;
  onCancelar: () => void;
  onConfirmar: (dados: DadosFechamento) => Promise<void> | void;
}) {
  const motivos = tipo === 'perdido' ? MOTIVOS_PERDA : MOTIVOS_NAO_PARTICIPAR;
  const [motivo, setMotivo] = useState<string>('');
  const [detalhe, setDetalhe] = useState('');
  const [vencedor, setVencedor] = useState('');
  const [precoVencedor, setPrecoVencedor] = useState<number | null>(null);
  const [valorContratado, setValorContratado] = useState<number | null>(valorEstimado != null ? Number(valorEstimado) : null);
  const [gravando, setGravando] = useState(false);

  const cabecalho = tipo === 'perdido' ? 'Marcar como perdida'
    : tipo === 'nao_participamos' ? 'Não vamos participar' : 'Registrar o contrato assinado';
  const okTexto = tipo === 'perdido' ? 'Marcar como perdida'
    : tipo === 'nao_participamos' ? 'Registrar que não participamos' : 'Registrar assinatura';
  const precisaDetalhe = motivo === 'Outro motivo' && !detalhe.trim();
  const podeConfirmar = tipo === 'ganho' ? (valorContratado != null && valorContratado > 0) : (!!motivo && !precisaDetalhe);

  const confirmar = async () => {
    setGravando(true);
    try {
      if (tipo === 'ganho') {
        await onConfirmar({ valor_contratado: valorContratado });
      } else {
        await onConfirmar({
          motivo: textoDoMotivo(motivo, detalhe),
          vencedor: tipo === 'perdido' ? vencedor.trim() || undefined : undefined,
          preco_vencedor: tipo === 'perdido' ? precoVencedor : undefined,
        });
      }
    } finally { setGravando(false); }
  };

  return (
    <Modal open title={cabecalho} onCancel={onCancelar} onOk={confirmar} destroyOnHidden
      okText={okTexto} cancelText="Voltar" confirmLoading={gravando}
      okButtonProps={{ disabled: !podeConfirmar, danger: tipo === 'perdido' }}
      width="94vw" style={{ maxWidth: 560, top: 24 }}>
      <Text strong style={{ display: 'block', marginBottom: 10 }}>{titulo}</Text>
      {tipo === 'ganho' ? (
        <Form layout="vertical" size="small">
          <Form.Item label="Valor contratado (R$)" extra="Sugerido o valor estimado do edital. É o que vai para a venda e para o contrato.">
            <InputNumber {...(PROPS_DINHEIRO_BR as any)} value={valorContratado} onChange={(v) => setValorContratado(v == null ? null : Number(v))}
              min={0} style={{ width: 240 }} />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            A licitação vai para "Contrato assinado" e o negócio gêmeo vira ganho — a venda e o kick-off do
            contrato seguem pelo caminho de sempre.
          </Text>
        </Form>
      ) : (
        <>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
            {tipo === 'perdido'
              ? 'Por que perdemos? Fica na licitação e é o que permite, depois, saber onde o funil vaza.'
              : 'Por que não vamos participar? A oportunidade fica registrada com a decisão.'}
          </Text>
          <Radio.Group value={motivo} onChange={e => setMotivo(e.target.value)} style={{ width: '100%' }}>
            <Space direction="vertical" size={4}>
              {motivos.map(m => <Radio key={m} value={m}>{m}</Radio>)}
            </Space>
          </Radio.Group>
          <Input.TextArea value={detalhe} onChange={e => setDetalhe(e.target.value)} rows={2} maxLength={240} showCount
            placeholder={motivo === 'Outro motivo' ? 'Escreva o motivo' : 'Detalhe (opcional)'}
            style={{ marginTop: 10 }} />
          {tipo === 'perdido' && (
            <Form layout="vertical" size="small" style={{ marginTop: 10 }}>
              <Space wrap>
                <Form.Item label="Quem ganhou" style={{ marginBottom: 0 }}>
                  <Input value={vencedor} onChange={e => setVencedor(e.target.value)} placeholder="Empresa vencedora" style={{ width: 260 }} maxLength={200} />
                </Form.Item>
                <Form.Item label="Preço vencedor (R$)" style={{ marginBottom: 0 }}>
                  <InputNumber {...(PROPS_DINHEIRO_BR as any)} value={precoVencedor} onChange={(v) => setPrecoVencedor(v == null ? null : Number(v))}
                    min={0} style={{ width: 180 }} />
                </Form.Item>
              </Space>
            </Form>
          )}
        </>
      )}
    </Modal>
  );
}
